import type { ElementRef, Signal } from '@angular/core';
import { DestroyRef, Directive, computed, effect, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import type { CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { switchMap } from 'rxjs';

import type { AnimationSettings } from '@components/sp-animation-settings/type/animation-settings.type';
import { getGsapAnimation } from '@components/sp-animation-settings/sp-animation-settings.gsap-getters';
import type { BsModalRef } from 'ngx-bootstrap/modal';
import { BsModalService } from 'ngx-bootstrap/modal';
import type { Uuid } from '@common/types';
import type { WidgetErrorItem } from '@common/types/widget-errors.type';
import type { WebWidgetModel, WebLegacyWidgetEntity } from '@web/types';
import type { DenormalizedColumnEntity, DenormalizedSectionEntity } from '@web/types';
import type { HoverState } from '@web/layout/content/page-content/directives/single-hover.directive';
import type { WidgetsErrorsState } from '@web/store/page-structure/widgets-errors/widgets-errors.reducer';

import { columnAnimation } from '@sites/entity-core';
import type { ColumnDefaultForm } from '@sites/entity-signal-form';

import { WebColumnFormService } from '@web/core/services/web-column-form.service';

import { ESelectedEntityType } from '@common/types/layout.type';
import { arrayLengthEq } from '@common/helpers';

import { AddWidgetOrGridComponent } from '@web/layout/content/page-content/components/add-widget-or-grid';
import { AddWidgetComponent } from '@web/layout/content/page-content/components/add-widget';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { NavigationDrawerService } from '@web/layout/drawers/navigation-drawer/navigation-drawer.service';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { TabsFacade } from '@web/store/page-structure/tabs/tabs.facade';
import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { WidgetsErrorsFacade } from '@web/store/page-structure/widgets-errors/widgets-errors.facade';
import { ProductRestrictionFacade } from '@web/core/services/product-restriction.facade';
import { DragPointerService } from '@web/core/services/drag-pointer.service';

import { Widgets } from '@common/enums';

export type ColumnType = 'default' | 'specific' | 'zero';

@Directive({})
export abstract class BaseColumnComponent<TSection extends DenormalizedSectionEntity = DenormalizedSectionEntity> {
    public readonly entity = input.required<DenormalizedColumnEntity>();
    public readonly parentEntity = input.required<TSection>();
    public readonly parentEntityIndex = input.required<number>();
    public readonly index = input.required<number>();
    public readonly disableColumnCopy = input<boolean>(false);
    public readonly isLastColumn = input<boolean>(false);
    public readonly blockId = input<string>('');

    protected readonly modalService: BsModalService = inject(BsModalService);
    protected readonly blocksFacade: BlocksFacade = inject(BlocksFacade);
    protected readonly sectionFacade: SectionsFacade = inject(SectionsFacade);
    protected readonly columnsFacade: ColumnsFacade = inject(ColumnsFacade);
    protected readonly columnFormService = inject(WebColumnFormService);
    protected readonly destroyRef = inject(DestroyRef);

    /** One entry per column id this component has written through — see {@link columnForm}. */
    private readonly columnForms = new Map<string, ReturnType<WebColumnFormService['getOrCreateScoped']>>();
    protected readonly tabsFacade: TabsFacade = inject(TabsFacade);
    protected readonly gridsFacade: GridsFacade = inject(GridsFacade);
    protected readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    protected readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    protected readonly widgetsErrorsFacade: WidgetsErrorsFacade = inject(WidgetsErrorsFacade);
    protected readonly productRestrictionFacade: ProductRestrictionFacade = inject(ProductRestrictionFacade);
    protected readonly navDrawerService: NavigationDrawerService = inject(NavigationDrawerService);
    private readonly dragPointer = inject(DragPointerService);

    private readonly selectedEntityId = toSignal(this.layoutFacade.entityId$);

    public readonly hideControls = toSignal(this.layoutFacade.hideControls$, { initialValue: false });
    /** §7's "preview mode" — see the same field on `BaseBlockComponent` for why `hovered()` is the one gate. */
    protected readonly isCanvasPreviewActive = toSignal(this.layoutFacade.isCanvasPreviewActive$, {
        initialValue: false,
    });
    public readonly isTablet = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });
    public readonly isMobile = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });

    public readonly isActive = computed<boolean>(() => this.selectedEntityId() === this.entity().id);

    public abstract readonly columnType: ColumnType;

    public readonly isSettingsMenuOpened = signal<boolean>(false);
    public readonly hoverState = signal<HoverState | null>(null);

    /** Every column on the page — one left means this one cannot be removed. */
    private readonly columnsIds = toSignal(this.columnsFacade.selectColumnsIds(), { initialValue: [] as string[] });

    /**
     * Whole error map rather than a per-widget selector: it is a plain record, so
     * one subscription serves every widget the column renders.
     */
    private readonly widgetsErrors = toSignal(this.widgetsErrorsFacade.widgetsErrors$, {
        initialValue: {} as WidgetsErrorsState,
    });

    protected readonly column$ = toObservable(this.entity);

    /**
     * In e-commerce mode a column holding a product widget is locked: it cannot
     * be copied, removed, or have widgets dropped into it.
     */
    public readonly isColumnRestricted = toSignal(
        this.column$.pipe(switchMap((column) => this.productRestrictionFacade.columnRestricted$(column))),
        { initialValue: false }
    );

    public readonly disableColumnRemove = computed<boolean>(
        () => this.isLastColumn() || arrayLengthEq<string>(this.columnsIds(), 1)
    );

    // `columnType` is fixed per subclass, so these stay plain getters — there is
    // nothing reactive to track.
    public get isSpecificColumn(): boolean {
        return this.columnType === 'specific';
    }

    public get isZeroColumn(): boolean {
        return this.columnType === 'zero';
    }

    public get isDefaultColumn(): boolean {
        return this.columnType === 'default';
    }

    public widgetErrorsById(id: Uuid): WidgetErrorItem {
        return this.widgetsErrors()[id];
    }

    /**
     * Replays the entrance animation whenever its settings change, so the editor
     * previews what was just picked. The first value seen is only recorded —
     * opening a page must not animate every column at once — and GSAP is handed
     * the children of `target`, which is what the animation actually moves.
     *
     * Call from a subclass constructor: it needs an injection context, and the
     * view child it animates has to be declared by then.
     */
    protected replayAnimationOnChange(target: Signal<ElementRef<HTMLElement>>): void {
        let previous: string | null = null;

        effect(() => {
            const animation = columnAnimation(this.entity());
            const current = animation ? JSON.stringify(animation) : null;
            const seen = previous;

            previous = current;

            if (!seen || !current || seen === current || !animation.isActive) {
                return;
            }

            // The schema types the animation fields as plain literal unions — the
            // package is framework-free and cannot see the host enums — but the
            // persisted values are exactly the enum members.
            getGsapAnimation(animation as AnimationSettings, target().nativeElement.childNodes);
        });
    }

    public copyColumn(): void {
        this.columnsFacade.copyColumn(this.entity(), this.index());
    }

    public removeColumn(): void {
        if (this.disableColumnRemove()) {
            return;
        }

        const columnSection = this.parentEntity();

        if (columnSection.children.length === 1) {
            this.sectionFacade.removeSection(columnSection, this.parentEntityIndex());
            return;
        }

        this.columnsFacade.removeColumn(this.entity(), this.index());
    }

    public selectColumn(): void {
        const column = this.entity();

        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.column, entityId: column.id });
        this.navDrawerService.selectInNavigation({
            entityId: column.id,
            sectionId: column.parentId,
            blockId: this.blockId(),
        });
    }

    public hovered(event: HoverState): void {
        this.hoverState.set(this.isCanvasPreviewActive() ? null : event);
    }

    public dropWidget(event: CdkDragDrop<WebLegacyWidgetEntity[]>): void {
        const { item, container, previousContainer, currentIndex, previousIndex } = event;

        if (this.isColumnRestricted()) {
            return;
        }

        // Dropped from a grid cell: the cell's list id is `grid_<gridId>_<cellIndex>` — the widget
        // itself carries neither.
        if (previousContainer.id.startsWith('grid')) {
            const [, gridId, cellIndex] = previousContainer.id.split('_');
            this.gridsFacade.moveWidgetFromGridToColumn(
                item.data.id,
                Number(cellIndex),
                gridId,
                this.entity().id,
                previousIndex,
                currentIndex
            );
            return;
        }

        if (container === previousContainer && previousIndex === currentIndex) {
            return;
        }

        const widget = item.data as WebLegacyWidgetEntity;
        this.widgetsFacade.moveWidgetToColumn(
            widget.id,
            previousContainer.id,
            container.id,
            previousIndex,
            currentIndex
        );
        this.animationHandler(widget, container.id);
    }

    // The column wraps its nested grids, so CDK would pick it over the grid cell the drag started in.
    // CDK also calls this once on drag start to decide whether the list may receive at all — always allow that call.
    public readonly enterPredicate = (_drag: unknown, drop: CdkDropList): boolean =>
        !drop._dropListRef.isReceiving() || !this.dragPointer.isOverGridInside(drop.element.nativeElement);

    public addWidget(blockHasLogo?): void {
        if (this.isSpecificColumn || this.isZeroColumn) {
            const modalRef: BsModalRef<AddWidgetComponent> = this.modalService.show(AddWidgetComponent, {
                backdrop: false,
                class: 'add-widgets-modal',
                initialState: {
                    widgetType: this.columnType,
                    isDisabledLogoWidget: blockHasLogo,
                },
            });
            modalRef.content.onClose.subscribe((widget: WebWidgetModel) => {
                if (!widget) {
                    return;
                }

                this.widgetsFacade.createWidget(this.entity().id, 0, widget.name, { type: this.columnType });
            });
            return;
        }

        const modalRef: BsModalRef<AddWidgetOrGridComponent> = this.modalService.show(AddWidgetOrGridComponent, {
            backdrop: false,
            class: 'add-widgets-modal',
            initialState: {
                isSpecific: this.isSpecificColumn,
                isDisabledLogoWidget: blockHasLogo,
            },
        });
        modalRef.content.onClose.subscribe((payload) => {
            if (!payload) {
                return;
            }

            if (payload.type === 'tab') {
                this.tabsFacade.create(this.entity().id, this.index() + 1, { variant: payload.variant });
                return;
            }

            if (payload.type === 'grid') {
                this.gridsFacade.create(this.entity().id, payload.variant, this.index() + 1);
                return;
            }

            if (!payload.entity) {
                return;
            }

            this.widgetsFacade.createWidget(this.entity().id, 0, payload.entity.name);
            this.animationHandler(payload.entity);
        });
    }

    public setSettingsMenuOpened(state: boolean): void {
        this.isSettingsMenuOpened.set(state);
    }

    public dragStarted(): void {
        this.layoutFacade.setIsEntityDragging(true);
    }

    public dragEnded(): void {
        this.layoutFacade.setIsEntityDragging(false);
    }

    /**
     * A product card brings its own entrance animation, so the column drops its.
     *
     * Through the *target* column's form. The previous version read `props` off
     * `this.entity()` and wrote the whole bag onto `columnId`, so a card dropped
     * into a neighbouring column replaced that column's entire appearance —
     * padding, background, contour, corners, shadow and width — with this one's.
     * A form write touches the one leaf it names.
     */
    private animationHandler(entity: { name?: string }, columnId: string | null = null): void {
        if (entity.name !== Widgets.PRODUCT_CARD && entity.name !== Widgets.CATALOG_PRODUCTS) {
            return;
        }

        const form = this.columnForm(columnId ?? this.entity().id);

        // Only an ordinary column can be animated at all; the others have no such setter.
        if ('setAnimationIsActive' in form) {
            (form as ColumnDefaultForm).setAnimationIsActive(false);
        }
    }

    /**
     * The column forms this component writes through, one per id. Scoped to the
     * component so the ref-count is released once, on destroy, rather than on
     * every drop.
     */
    private columnForm(id: string) {
        const existing = this.columnForms.get(id);
        if (existing) {
            return existing;
        }

        const form = this.columnFormService.getOrCreateScoped(id, this.destroyRef);
        this.columnForms.set(id, form);

        return form;
    }
}
